import React from 'react';
import { StyleSheet, View } from 'react-native';

type IconProps = { color: string; focused?: boolean };

/** Same home glyph used on Dashboard / Admin metric cards. */
export function TabHomeIcon({ color }: IconProps) {
  return (
    <View style={styles.root} accessibilityElementsHidden>
      <View style={[styles.homeRoof, { borderBottomColor: color }]} />
      <View style={[styles.homeBody, { borderColor: color }]} />
    </View>
  );
}

/** Same clock glyph used on Admin Dashboard attendance metrics. */
export function TabClockIcon({ color }: IconProps) {
  return (
    <View style={styles.root} accessibilityElementsHidden>
      <View style={[styles.clockRing, { borderColor: color }]}>
        <View style={[styles.clockHand, { backgroundColor: color }]} />
      </View>
    </View>
  );
}

/** Same calendar glyph used on Attendance Report. */
export function TabCalendarIcon({ color }: IconProps) {
  return (
    <View style={styles.root} accessibilityElementsHidden>
      <View style={[styles.calendarBody, { borderColor: color }]}>
        <View style={[styles.calendarTop, { backgroundColor: color }]} />
        <View style={styles.calendarDots}>
          <View style={[styles.calendarDot, { backgroundColor: color }]} />
          <View style={[styles.calendarDot, { backgroundColor: color }]} />
          <View style={[styles.calendarDot, { backgroundColor: color }]} />
        </View>
      </View>
    </View>
  );
}

/** Same person glyph used on Employee list avatars / Admin metrics. */
export function TabPersonIcon({ color }: IconProps) {
  return (
    <View style={styles.root} accessibilityElementsHidden>
      <View style={[styles.personHead, { borderColor: color }]} />
      <View style={[styles.personBody, { borderColor: color }]} />
    </View>
  );
}

/** Same briefcase glyph used for Leave / On Leave metrics. */
export function TabBriefcaseIcon({ color }: IconProps) {
  return (
    <View style={styles.root} accessibilityElementsHidden>
      <View style={[styles.briefCase, { borderColor: color }]}>
        <View style={[styles.briefHandle, { borderColor: color }]} />
      </View>
    </View>
  );
}

/** Menu — three lines matching the app’s filter-line icon language. */
export function TabMenuIcon({ color }: IconProps) {
  return (
    <View style={styles.menuRoot} accessibilityElementsHidden>
      <View style={[styles.menuLine, { backgroundColor: color }]} />
      <View style={[styles.menuLine, styles.menuMid, { backgroundColor: color }]} />
      <View style={[styles.menuLine, { backgroundColor: color }]} />
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  homeRoof: {
    width: 0,
    height: 0,
    borderLeftWidth: 8,
    borderRightWidth: 8,
    borderBottomWidth: 7,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    marginBottom: 1,
  },
  homeBody: {
    width: 14,
    height: 9,
    borderWidth: 2,
    borderTopWidth: 0,
  },
  clockRing: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 3,
  },
  clockHand: {
    width: 2,
    height: 6,
    borderRadius: 1,
  },
  calendarBody: {
    width: 18,
    height: 16,
    borderWidth: 1.8,
    borderRadius: 3,
    overflow: 'hidden',
  },
  calendarTop: {
    height: 4,
    opacity: 0.9,
  },
  calendarDots: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    paddingHorizontal: 2,
  },
  calendarDot: {
    width: 2.5,
    height: 2.5,
    borderRadius: 1.5,
  },
  personHead: {
    width: 9,
    height: 9,
    borderRadius: 5,
    borderWidth: 2,
    marginBottom: 2,
  },
  personBody: {
    width: 16,
    height: 9,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderWidth: 2,
    borderBottomWidth: 0,
  },
  briefCase: {
    width: 18,
    height: 12,
    borderWidth: 2,
    borderRadius: 2,
    alignItems: 'center',
  },
  briefHandle: {
    width: 8,
    height: 4,
    borderWidth: 2,
    borderBottomWidth: 0,
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2,
    marginTop: -4,
  },
  menuRoot: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3.5,
  },
  menuLine: {
    width: 16,
    height: 2,
    borderRadius: 1,
  },
  menuMid: {
    width: 12,
  },
});
